import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { actualizarUsuario } from "../../../services/apis";
import { AlertError } from "../../../utils/componentes-utiles/AlertError";
import { ContasenaOculta } from "../../../utils/componentes-utiles/ContasenaOculta";
import { ClienteMultiSelect } from "./ClienteMultiSelect";
import { DelegacionMultiSelect } from "./DelegacionMultiSelect";
import { CampanaMultiSelect } from "./CampanaMultiSelect";

const ROL_CLIENTE = 990;
const ROL_CONTROLADOR = 980;

const normalizeRoleName = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const EditarUsuario = ({
	open,
	setOpen,
	cargarUsuarios,
	roles = [],
	departamentos = [],
	clientes = [],
	delegaciones = [],
	campanas = [],
	usuarioData,
}) => {
	const [usuario, setUsuario] = useState({
		id: "",
		nombre: "",
		apellidos: "",
		mail: "",
		id_rol: "",
		id_departamento: "",
		activo: 1,
		color: "#1976d2",
		telefono: "",
		nif: "",
		id_clientes: [],
		id_delegaciones: [],
		id_campanas: [],
	});
	const [color, setColor] = useState("#1976d2");
	const [contrasena, setContrasena] = useState("");
	const [confirmarContrasena, setConfirmarContrasena] = useState("");
	const [cambiarContrasena, setCambiarContrasena] = useState(false);
	const [error, setError] = useState("");
	const [cargando, setCargando] = useState(false);

	const handleClose = (force = false) => {
		if (cargando && !force) {
			return;
		}

		setOpen(false);
	};

	useEffect(() => {
		if (!usuarioData || !open) {
			return;
		}

		setUsuario({
			id: usuarioData.id,
			nombre: usuarioData.nombre || "",
			apellidos: usuarioData.apellidos || "",
			mail: usuarioData.mail || "",
			id_rol: usuarioData.rol || usuarioData.id_rol || "",
			id_departamento: usuarioData.departamento || usuarioData.id_departamento || "",
			activo: Number(usuarioData.activo ?? 1) === 1 ? 1 : 0,
			color: usuarioData.color || "#1976d2",
			telefono: usuarioData.telefono || "",
			nif: usuarioData.nif || "",
			id_clientes: Array.isArray(usuarioData?.clientes_asignados_ids)
				? usuarioData.clientes_asignados_ids.map((item) => Number(item)).filter((item) => item > 0)
				: [],
			id_delegaciones: Array.isArray(usuarioData?.delegaciones_asignadas_ids)
				? usuarioData.delegaciones_asignadas_ids
						.map((item) => Number(item))
						.filter((item) => item > 0)
				: [],
			id_campanas: Array.isArray(usuarioData?.campanas_asignadas_ids)
				? usuarioData.campanas_asignadas_ids.map((item) => Number(item)).filter((item) => item > 0)
				: [],
		});
		setColor(usuarioData.color || "#1976d2");
		setCambiarContrasena(false);
		setContrasena("");
		setConfirmarContrasena("");
		setError("");
		setCargando(false);
	}, [open, usuarioData]);

	const rolSeleccionado = useMemo(
		() => roles.find((rol) => Number(rol?.id) === Number(usuario.id_rol || 0)) || null,
		[roles, usuario.id_rol]
	);
	const esCliente = Number(usuario.id_rol || 0) === ROL_CLIENTE;
	const esControlador = Number(usuario.id_rol || 0) === ROL_CONTROLADOR;
	const esCaboCliente = normalizeRoleName(rolSeleccionado?.nombre) === "cabo cliente";

	const clientesAsignadosTexto = useMemo(() => {
		if ((!esCliente && !esCaboCliente && !esControlador) || usuario.id_clientes.length === 0) {
			return "";
		}

		return clientes
			.filter((cliente) => usuario.id_clientes.includes(Number(cliente.id)))
			.map((cliente) => cliente.nombre)
			.filter(Boolean)
			.join(", ");
	}, [clientes, esCaboCliente, esCliente, esControlador, usuario.id_clientes]);

	const delegacionesAsignadasTexto = useMemo(() => {
		if ((!esCaboCliente && !esControlador) || usuario.id_delegaciones.length === 0) {
			return "";
		}

		return delegaciones
			.filter((delegacion) => usuario.id_delegaciones.includes(Number(delegacion.id)))
			.map((delegacion) => delegacion.nombre)
			.filter(Boolean)
			.join(", ");
	}, [delegaciones, esCaboCliente, esControlador, usuario.id_delegaciones]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setUsuario((current) => {
			const nextUsuario = {
				...current,
				[name]: value,
			};

			const selectedRole = roles.find((rol) => Number(rol?.id) === Number(value)) || null;
			const nextRoleName = normalizeRoleName(selectedRole?.nombre);

			if (
				name === "id_rol" &&
				Number(value) !== ROL_CLIENTE &&
				Number(value) !== ROL_CONTROLADOR &&
				nextRoleName !== "cabo cliente"
			) {
				nextUsuario.id_clientes = [];
			}

			if (
				name === "id_rol" &&
				Number(value) !== ROL_CONTROLADOR &&
				nextRoleName !== "cabo cliente"
			) {
				nextUsuario.id_delegaciones = [];
			}

			return nextUsuario;
		});
		setError("");
	};

	const handleSubmit = async () => {
		if (!usuario.nombre || !usuario.mail || !usuario.id_rol) {
			setError("Nombre, email y rol son obligatorios");
			return;
		}

		if (cambiarContrasena && contrasena !== confirmarContrasena) {
			setError("Las contraseñas no coinciden");
			return;
		}

		const payload = {
			...usuario,
			color,
			id_clientes: esCliente || esCaboCliente || esControlador ? usuario.id_clientes : [],
			id_delegaciones: esCaboCliente || esControlador ? usuario.id_delegaciones : [],
			id_campanas: usuario.id_campanas,
		};

		if (cambiarContrasena && contrasena) {
			payload.contrasena = contrasena;
		}

		try {
			setCargando(true);
			const response = await actualizarUsuario(payload);

			if (response?.success) {
				await cargarUsuarios();
				handleClose(true);
			} else {
				setError(response?.error || "Error al actualizar usuario");
			}
		} catch (submitError) {
			console.error("Error actualizando usuario:", submitError);
			setError("Error de conexion con el servidor");
		} finally {
			setCargando(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<Grid container justifyContent="space-between" alignItems="center">
				<DialogTitle>Editar Usuario</DialogTitle>
				<CancelIcon onClick={handleClose} style={{ cursor: "pointer", marginRight: 16 }} />
			</Grid>

			<DialogContent>
				<Grid container spacing={2}>
					<Grid container justifyContent="center" size={{ xs: 12 }}>
						<AccountCircleIcon sx={{ fontSize: 90, color }} />
						<input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Nombre" fullWidth name="nombre" value={usuario.nombre} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Apellidos" fullWidth name="apellidos" value={usuario.apellidos} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12 }}>
						<TextField label="Email" fullWidth name="mail" value={usuario.mail} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="Telefono" fullWidth name="telefono" value={usuario.telefono} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<TextField label="NIF" fullWidth name="nif" value={usuario.nif} onChange={handleChange} />
					</Grid>

					<Grid item size={{ xs: 12, md: 6 }}>
						<FormControl fullWidth>
							<InputLabel>Rol</InputLabel>
							<Select name="id_rol" value={usuario.id_rol} onChange={handleChange} label="Rol">
								{roles.map((rol) => (
									<MenuItem key={rol.id} value={rol.id}>
										{rol.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item size={{ xs: 12, md: 6 }}>
						<FormControl fullWidth>
							<InputLabel>Estado</InputLabel>
							<Select name="activo" value={usuario.activo} onChange={handleChange} label="Estado">
								<MenuItem value={1}>Activo</MenuItem>
								<MenuItem value={0}>Inactivo</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					{(esCliente || esCaboCliente || esControlador) && (
						<Grid item size={{ xs: 12 }}>
							<ClienteMultiSelect
								clientes={clientes}
								value={usuario.id_clientes}
								onChange={(id_clientes) => setUsuario((current) => ({ ...current, id_clientes }))}
								helperText={
									esControlador
										? "El controlador vera los partes de los clientes asignados."
										: esCaboCliente
										? "Este perfil vera los partes de los clientes asignados."
										: "El usuario cliente solo vera los partes de los clientes asignados."
								}
							/>
						</Grid>
					)}

					{(esCaboCliente || esControlador) && (
						<Grid item size={{ xs: 12 }}>
							<DelegacionMultiSelect
								delegaciones={delegaciones}
								value={usuario.id_delegaciones}
								onChange={(id_delegaciones) =>
									setUsuario((current) => ({ ...current, id_delegaciones }))
								}
								helperText={
									esControlador
										? "El controlador tambien vera los partes de las delegaciones asignadas."
										: "Tambien vera los partes de las delegaciones asignadas."
								}
							/>
						</Grid>
					)}

					<Grid item size={{ xs: 12 }}>
						<CampanaMultiSelect
							campanas={campanas}
							value={usuario.id_campanas}
							onChange={(id_campanas) =>
								setUsuario((current) => ({ ...current, id_campanas }))
							}
						/>
					</Grid>

					{(esCliente || esCaboCliente || esControlador) && clientesAsignadosTexto && (
						<Grid item size={{ xs: 12 }}>
							<Typography variant="body2" color="text.secondary">
								Clientes asignados: {clientesAsignadosTexto}
							</Typography>
						</Grid>
					)}

					{(esCaboCliente || esControlador) && delegacionesAsignadasTexto && (
						<Grid item size={{ xs: 12 }}>
							<Typography variant="body2" color="text.secondary">
								Delegaciones asignadas: {delegacionesAsignadasTexto}
							</Typography>
						</Grid>
					)}

					<Grid item size={{ xs: 12 }}>
						<FormControlLabel
							control={
								<Switch
									checked={cambiarContrasena}
									onChange={() => setCambiarContrasena(!cambiarContrasena)}
								/>
							}
							label="Cambiar contraseña"
						/>
					</Grid>

					{cambiarContrasena && (
						<>
							<Grid item size={{ xs: 12, md: 6 }}>
								<ContasenaOculta contrasena={contrasena} setContrasena={setContrasena} />
							</Grid>
							<Grid item size={{ xs: 12, md: 6 }}>
								<ContasenaOculta contrasena={confirmarContrasena} setContrasena={setConfirmarContrasena} />
							</Grid>
						</>
					)}
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose}>Cancelar</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={cargando}>
					{cargando ? "Guardando..." : "Guardar cambios"}
				</Button>
			</DialogActions>

			{error ? <AlertError open={true} setOpen={() => setError("")} titulo="Error" mensaje={error} /> : null}
		</Dialog>
	);
};
